/**
 * VendorTab - Orchestrates vendor entity management view
 * Shows onboarding prompt if no entities claimed yet,
 * otherwise shows entity selector + form + menu manager
 * @atomic @idempotent
 */

import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { TagIcon, CalendarIcon, WrenchScrewdriverIcon, InboxIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { useVendorStatus } from '../../hooks/useVendorStatus'
import { useVendorBookings } from '../../hooks/useVendorBookings'
import { channelsAPI } from '../../services/api'
import { createLogger } from '../../utils/logger'
import VendorOnboardingPrompt from './VendorOnboardingPrompt'
import VendorEntityCard from './VendorEntityCard'
import VendorEntityForm from './VendorEntityForm'
import VendorMenuManager from './VendorMenuManager'
import VendorScheduleManager from './VendorScheduleManager'
import StewardWelcomeCard from './StewardWelcomeCard'
import VendorBookingQueue from '../bookings/VendorBookingQueue'

const logger = createLogger('VendorTab')

const VendorTab = memo(function VendorTab({ onOpenContact, onOpenChannel }) {
  const { t } = useTranslation()
  const { isVendor, vendorEntities, loading, refetch } = useVendorStatus()
  const [selectedEntity, setSelectedEntity] = useState(null)
  const [activeSection, setActiveSection] = useState('menu') // 'menu' | 'details' | 'schedule'
  const [welcomeDismissed, setWelcomeDismissed] = useState(false)

  const dismissWelcome = useCallback((entityId) => {
    if (!entityId) return
    try { localStorage.setItem(`steward_welcome_dismissed_${entityId}`, '1') } catch (_) { /* ignore */ }
    setWelcomeDismissed(true)
  }, [])

  const handleSelectEntity = useCallback((entity) => {
    setSelectedEntity(entity)
    setActiveSection('menu')
    // Check per-entity dismissal state
    try {
      const dismissed = localStorage.getItem(`steward_welcome_dismissed_${entity.id}`)
      setWelcomeDismissed(!!dismissed)
    } catch (_) {
      setWelcomeDismissed(false)
    }
  }, [])

  const handleOpenChannel = useCallback(async (entity) => {
    if (entity.channelId) {
      onOpenChannel?.(entity.channelId)
      return
    }
    // Channel not pre-loaded — create/join it on demand (idempotent)
    try {
      const res = await channelsAPI.getEntityChannel(entity.id)
      const channelId = res.data?.channelId
      if (channelId) onOpenChannel?.(channelId)
    } catch (err) {
      logger.error('Failed to ensure entity channel', err)
    }
  }, [onOpenChannel])

  const handleEntitySaved = useCallback((updatedEntity) => {
    setSelectedEntity(updatedEntity)
    refetch()
  }, [refetch])

  const handleReleased = useCallback((entityId) => {
    // Deselect if the released entity was selected, then refresh the list
    setSelectedEntity(prev => prev?.id === entityId ? null : prev)
    refetch()
  }, [refetch])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-sm text-gray-400">
        {t('common.loading', 'Loading…')}
      </div>
    )
  }

  if (!isVendor || vendorEntities.length === 0) {
    return <VendorOnboardingPrompt onOpenContact={onOpenContact} />
  }

  const sections = [
    { key: 'menu',     label: t('vendor.offerings.title', 'Offerings'),  Icon: TagIcon },
    { key: 'details',  label: t('vendor.form.title', 'Details'),           Icon: PencilSquareIcon },
    { key: 'schedule', label: t('schedule.title', 'Schedule'),             Icon: CalendarIcon },
    { key: 'bookings', label: t('bookings.vendorQueue', 'Bookings'),       Icon: InboxIcon },
  ]

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Single scroll column — entity card scrolls away, tabs become sticky */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">

        {/* Entity selector — scrolls with content */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            {t('vendor.entityList.title', 'Your listings')}
          </p>
          <div className="space-y-2">
            {vendorEntities.map(entity => (
              <VendorEntityCard
                key={entity.id}
                entity={entity}
                isSelected={selectedEntity?.id === entity.id}
                onSelect={handleSelectEntity}
                onOpenChannel={handleOpenChannel}
                onReleased={handleReleased}
              />
            ))}
          </div>
        </div>

        {selectedEntity ? (
          <>
            {/* New-steward welcome card */}
            {!welcomeDismissed && (
              <StewardWelcomeCard
                entityName={selectedEntity.name}
                onDismiss={() => dismissWelcome(selectedEntity.id)}
              />
            )}

            {/* Section tab bar — sticky so it stays visible once the entity card scrolls away */}
            <div className="flex sticky top-0 z-10 bg-white border-b border-gray-100">
              {sections.map(({ key, label, Icon }) => {
                const isActive = activeSection === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveSection(key)}
                    className={`relative flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors outline-none focus:outline-none focus:ring-0 ${
                      isActive ? 'text-primary border-b-2 border-primary bg-white' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                )
              })}
            </div>

            {/* Section content — min-h ensures it's always reachable by scroll */}
            <div className="min-h-[60vh]">
              {activeSection === 'menu' && (
                <VendorMenuManager entityId={selectedEntity.id} />
              )}
              {activeSection === 'details' && (
                <VendorEntityForm
                  entity={selectedEntity}
                  onSaved={handleEntitySaved}
                  onCancel={() => setActiveSection('menu')}
                />
              )}
              {activeSection === 'schedule' && (
                <VendorScheduleManager
                  entityId={selectedEntity.id}
                  t={t}
                  onSaved={refetch}
                />
              )}
              {activeSection === 'bookings' && (
                <div className="p-4">
                  <VendorBookingQueue entityId={selectedEntity.id} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center text-sm text-gray-400 p-6 text-center min-h-[40vh]">
            {t('vendor.entityList.selectPrompt', 'Select a listing above to manage it')}
          </div>
        )}

      </div>
    </div>
  )
})

export default VendorTab
